import type { DrawerTransport, PeripheralDevice, ConnectionState } from "./transport";
import { WebUsbDrawerTransport, isSupported as usbSupported } from "./webusb-transport";
import {
  WebBluetoothDrawerTransport,
  isSupported as bluetoothSupported,
} from "./webbluetooth-transport";

/**
 * Which bus this terminal's drawer is on.
 *
 * A TERMINAL-LOCAL FACT, NOT A SYNCED SETTING. The printer sitting under one
 * counter says nothing about the one under another, and syncing this would let
 * a change made at one till break the drawer at a second. It lives in
 * localStorage for that reason, and because the choice must be readable
 * synchronously at module construction — before IndexedDB is open.
 */
export type TransportKind = "usb" | "bluetooth";

const STORAGE_KEY = "hinto.peripherals.transport";

export function storedTransportKind(): TransportKind | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === "usb" || raw === "bluetooth" ? raw : null;
  } catch {
    // Private mode, disabled storage, a locked-down kiosk profile. Falling back
    // to detection is strictly better than failing the till load over a
    // preference.
    return null;
  }
}

/**
 * Pick a bus when nobody has chosen one.
 *
 * USB WINS WHEN BOTH ARE PRESENT, and that ordering is deliberate rather than
 * alphabetical: the USB grant reconnects silently after a reload, where the
 * Bluetooth grant frequently does not, and a drawer that needs re-pairing at
 * the start of every shift is a drawer staff will stop relying on.
 */
function detectKind(): TransportKind {
  if (usbSupported()) return "usb";
  if (bluetoothSupported()) return "bluetooth";
  // Neither bus exists. Returning "usb" hands back a transport that honestly
  // reports `isSupported() === false`, which is what capability detection wants.
  return "usb";
}

export function activeTransportKind(): TransportKind {
  return storedTransportKind() ?? detectKind();
}

function build(kind: TransportKind): DrawerTransport {
  return kind === "bluetooth" ? new WebBluetoothDrawerTransport() : new WebUsbDrawerTransport();
}

let currentKind: TransportKind = activeTransportKind();
let current: DrawerTransport = build(currentKind);

/**
 * Switch buses.
 *
 * Disconnects the outgoing transport first. Two transports each holding a claim
 * on the same physical printer is exactly the fight FR-108 puts everything
 * behind one boundary to avoid.
 */
export async function setTransportKind(kind: TransportKind): Promise<DrawerTransport> {
  if (kind === currentKind) return current;

  await current.disconnect();
  currentKind = kind;
  current = build(kind);

  try {
    localStorage.setItem(STORAGE_KEY, kind);
  } catch {
    // The choice still applies for this session; it just will not survive a
    // reload. Better than refusing to switch at all.
  }

  return current;
}

/**
 * The transport everything else holds.
 *
 * A STABLE FACADE, not the concrete instance. `drawer-status.ts` and the module
 * composition root capture this object once at construction; without the
 * indirection, switching buses would leave the status monitor polling a
 * transport nobody writes to any more, reporting a drawer state that belongs to
 * a printer that is no longer in use.
 *
 * This deliberately exposes no more of the surface than `DrawerTransport` does.
 * In particular it does not widen access to `kick()` — the closed trigger list
 * in `drawer-module.ts` is still the only path to it.
 */
export const activeTransport: DrawerTransport = {
  isSupported: () => current.isSupported(),
  requestPermission: (): Promise<PeripheralDevice> => current.requestPermission(),
  connect: (): Promise<ConnectionState> => current.connect(),
  isConnected: () => current.isConnected(),
  hasDrawerStatus: () => current.hasDrawerStatus(),
  kick: (pin?: 2 | 5) => current.kick(pin),
  readDrawerState: () => current.readDrawerState(),
  write: (bytes: Uint8Array) => current.write(bytes),
  disconnect: () => current.disconnect(),
};

/** Used as the default in capability detection. */
export function selectTransport(): DrawerTransport {
  return activeTransport;
}

/**
 * Which buses this browser exposes at all.
 *
 * The settings screen shows every kind and disables the ones missing here,
 * rather than hiding them. A person hunting for "why is Bluetooth not an
 * option" is better served by a greyed-out row than by an absent one.
 */
export function supportedKinds(): Record<TransportKind, boolean> {
  return { usb: usbSupported(), bluetooth: bluetoothSupported() };
}
